
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { Search, Plus, Camera, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface NoMatchProductProps {
  detectedText?: string;
  productInfo?: {
    brand?: string;
    productName?: string;
    volume?: string;
    ingredients?: string[];
  };
  onClose: () => void;
  onScanAgain: () => void;
}

const NoMatchProduct = ({ detectedText, productInfo, onClose, onScanAgain }: NoMatchProductProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isChecking, setIsChecking] = useState(false);

  const handleSearch = async () => {
    if (productInfo?.brand || detectedText) {
      // First check if we can find the product in our database
      try {
        setIsChecking(true);
        const searchTerm = productInfo?.brand || detectedText?.split('\n')[0] || '';
        
        if (searchTerm.length > 0) {
          const { data: products, error } = await supabase
            .from('products')
            .select('id, name, brand')
            .or(`brand.ilike.%${searchTerm}%,name.ilike.%${searchTerm}%`)
            .limit(1);
            
          if (!error && products && products.length > 0) {
            // If we found a product, navigate to it
            toast({
              title: t("products.productFound"),
              description: t("products.foundInDatabase", { name: products[0].name }),
            });
            navigate(`/product/${products[0].id}`);
          } else {
            // Otherwise do a regular search
            navigate(`/?q=${encodeURIComponent(searchTerm)}`);
          }
        } else {
          navigate('/');
        }
      } catch (error) {
        console.error('Error checking for product:', error);
        // Fall back to regular search
        const searchTerm = productInfo?.brand || detectedText?.split('\n')[0] || '';
        navigate(`/?q=${encodeURIComponent(searchTerm)}`);
      } finally {
        setIsChecking(false);
        onClose();
      }
    } else {
      // If no brand or text, just go to home
      navigate('/');
      onClose();
    }
  };

  const handleAddNew = () => {
    navigate('/product/new', { 
      state: { 
        detectedText,
        productInfo
      }
    });
    onClose();
  };

  return (
    <div className="space-y-5 p-6">
      <div className="flex items-center mb-2">
        <Button 
          variant="ghost" 
          size="icon" 
          className="mr-2" 
          onClick={onScanAgain}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h3 className="text-lg font-semibold text-amber-600">
          {t("products.noMatch")}
        </h3>
      </div>
      
      <p className="text-sm text-gray-500">
        {t("products.weFoundText")}
      </p>

      {productInfo && (Object.values(productInfo).some(v => v && (typeof v === 'string' ? v.length > 0 : v.length > 0))) && (
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-100">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-2">
            {t("products.detectedInfo")}:
          </p>
          <div className="space-y-1">
            {productInfo.brand && (
              <p className="text-sm"><span className="font-medium">{t("products.brand")}:</span> {productInfo.brand}</p>
            )}
            {productInfo.productName && (
              <p className="text-sm"><span className="font-medium">{t("products.name")}:</span> {productInfo.productName}</p>
            )}
            {productInfo.volume && (
              <p className="text-sm"><span className="font-medium">{t("products.volume")}:</span> {productInfo.volume}</p>
            )}
          </div>
        </div>
      )}

      {detectedText && !productInfo && (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
          <p className="text-sm font-medium mb-1">{t("products.detectedText")}:</p>
          <p className="text-sm text-gray-600 break-words">{detectedText}</p>
        </div>
      )}

      <div className="flex flex-col gap-3 pt-2">
        <Button
          variant="outline"
          className="w-full h-11 border-amber-200 hover:bg-amber-50 text-amber-800 rounded-lg"
          onClick={handleSearch}
          disabled={isChecking}
        >
          {isChecking ? (
            <>
              <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-amber-600 border-t-transparent"></div>
              {t("common.checking")}
            </>
          ) : (
            <>
              <Search className="w-4 h-4 mr-2" />
              {t("products.searchManually")}
            </>
          )}
        </Button>

        <Button
          variant="default"
          className="w-full h-11 bg-amber-600 hover:bg-amber-700 text-white rounded-lg"
          onClick={handleAddNew}
        >
          <Plus className="w-4 h-4 mr-2" />
          {t("products.addNewProduct")}
        </Button>
        
        <Button
          variant="ghost"
          className="w-full h-11 text-gray-600 hover:bg-gray-100 rounded-lg"
          onClick={onScanAgain}
        >
          <Camera className="w-4 h-4 mr-2" />
          {t("products.scanAgain")}
        </Button>
      </div>
    </div>
  );
};

export default NoMatchProduct;
